import { useAtomValue } from "jotai";
import { useFlags } from "@/utils/flagsmith/useFlags";
import { DetailTableRow } from "../../components/DetailTable";
import { useColorizedValue } from "../useColorizedValue";
import { balanceSheetMetricsAtom } from "../../metrics.state";
import {
    FinanceLabField,
    FinanceLabFieldType,
    formatFinanceLabValue,
} from "@/entities/FinanceLab";
import { getLastValue } from "@/utils/metrics/metricsUtils";
import { AccountLabel } from "@/entities/Labeling/Account.model";
import { getMetric } from "../../utils/getMetric";
import { scrollAndHighlightRow } from "../../utils/scrollAndHighlight";
import { useMetricClickHandler } from "../shared/useMetricClickHandler";
import { createMetricLabel } from "../../utils/labelHelpers";

export function useBalanceSheetData(): DetailTableRow[] {
    const flags = useFlags();
    const showM05 = flags.enabled("show-im-m05");
    const handleMetricClick = useMetricClickHandler();
    const balanceSheetMetrics = useAtomValue(balanceSheetMetricsAtom);

    const cash = getMetric(
        balanceSheetMetrics,
        AccountLabel.CURRENT_ASSETS_CASH_AND_CASH_EQUIVALENTS,
        FinanceLabField.ORGANIZATION_FINANCIAL_DATA_CASH,
    );
    const totalAssets = getMetric(
        balanceSheetMetrics,
        AccountLabel.ASSETS,
        FinanceLabField.ORGANIZATION_FINANCIAL_DATA_TOTAL_ASSETS,
    );
    const grossPPE = getMetric(
        balanceSheetMetrics,
        AccountLabel.NONCURRENT_ASSETS_PROPERTY_PLANT_AND_EQUIPMENT,
    );
    const totalLiabilities = getMetric(
        balanceSheetMetrics,
        AccountLabel.LIABILITIES,
        FinanceLabField.ORGANIZATION_FINANCIAL_DATA_TOTAL_LIABILITIES,
    );
    const accountsReceivable = getMetric(
        balanceSheetMetrics,
        AccountLabel.CURRENT_ASSETS_NET_RECEIVABLES,
    );
    const accountsPayable = getMetric(
        balanceSheetMetrics,
        AccountLabel.CURRENT_LIABILITIES_ACCOUNTS_PAYABLE,
    );
    const inventory = getMetric(
        balanceSheetMetrics,
        AccountLabel.CURRENT_ASSETS_NET_INVENTORY,
    );
    const currentDebt = getMetric(
        balanceSheetMetrics,
        AccountLabel.CURRENT_LIABILITIES_DEBT,
    );

    const assetsColorized = useColorizedValue(
        formatFinanceLabValue(
            getLastValue(totalAssets?.values)?.value,
            FinanceLabFieldType.CURRENCY,
        ) || "-",
        totalAssets?.source,
        totalAssets?.info.derived.label,
    );
    const grossPPEColorized = useColorizedValue(
        formatFinanceLabValue(
            getLastValue(grossPPE?.values)?.value,
            FinanceLabFieldType.CURRENCY,
        ) || "-",
        grossPPE?.source,
        grossPPE?.info.derived.label,
    );
    const liabilitiesColorized = useColorizedValue(
        formatFinanceLabValue(
            getLastValue(totalLiabilities?.values)?.value,
            FinanceLabFieldType.CURRENCY,
        ) || "-",
        totalLiabilities?.source,
        totalLiabilities?.info.derived.label,
    );

    const cashBalanceColorized = useColorizedValue(
        formatFinanceLabValue(
            getLastValue(cash?.values)?.value,
            FinanceLabFieldType.CURRENCY,
        ) || "-",
        cash?.source,
        cash?.info.derived.label,
    );

    const accountsReceivableColorized = useColorizedValue(
        formatFinanceLabValue(
            getLastValue(accountsReceivable?.values)?.value,
            FinanceLabFieldType.CURRENCY,
        ) || "-",
        accountsReceivable?.source,
        accountsReceivable?.info.derived.label,
    );
    const accountsPayableColorized = useColorizedValue(
        formatFinanceLabValue(
            getLastValue(accountsPayable?.values)?.value,
            FinanceLabFieldType.CURRENCY,
        ) || "-",
        accountsPayable?.source,
        accountsPayable?.info.derived.label,
    );
    const inventoryColorized = useColorizedValue(
        formatFinanceLabValue(
            getLastValue(inventory?.values)?.value,
            FinanceLabFieldType.CURRENCY,
        ) || "-",
        inventory?.source,
        inventory?.info.derived.label,
    );
    const currentDebtColorized = useColorizedValue(
        formatFinanceLabValue(
            getLastValue(currentDebt?.values)?.value,
            FinanceLabFieldType.CURRENCY,
        ) || "-",
        currentDebt?.source,
        currentDebt?.info.derived.label,
    );
    return [
        {
            label: createMetricLabel(
                "Total Assets",
                totalAssets?.info.derived.localLabel,
            ),
            value: assetsColorized.value,
            valueClassName: assetsColorized.className,
            source: assetsColorized.source,
            sourceClassName: assetsColorized.sourceClassName,
            delta: totalAssets?.delta,
            onClick: () =>
                showM05
                    ? scrollAndHighlightRow(
                          "summary-balance-sheet",
                          "Total Assets",
                      )
                    : handleMetricClick(totalAssets, "Total Assets"),
        },
        {
            label: createMetricLabel(
                "Gross PP&E",
                grossPPE?.info.derived.localLabel,
            ),
            value: grossPPEColorized.value,
            valueClassName: grossPPEColorized.className,
            source: grossPPEColorized.source,
            sourceClassName: grossPPEColorized.sourceClassName,
            delta: grossPPE?.delta,
            onClick: () => handleMetricClick(grossPPE, "Gross PP&E"),
        },
        {
            label: createMetricLabel(
                "Cash & Equivalents",
                cash?.info.derived.localLabel,
            ),
            value: cashBalanceColorized.value,
            valueClassName: cashBalanceColorized.className,
            source: cashBalanceColorized.source,
            sourceClassName: cashBalanceColorized.sourceClassName,
            delta: cash?.delta,
            onClick: () =>
                showM05
                    ? scrollAndHighlightRow(
                          "summary-balance-sheet",
                          "Cash & Equivalents",
                      )
                    : handleMetricClick(cash, "Cash & Equivalents"),
        },
        {
            label: createMetricLabel(
                "Total Liabilities",
                totalLiabilities?.info.derived.localLabel,
            ),
            value: liabilitiesColorized.value,
            valueClassName: liabilitiesColorized.className,
            source: liabilitiesColorized.source,
            sourceClassName: liabilitiesColorized.sourceClassName,
            delta: totalLiabilities?.delta,
            onClick: () =>
                showM05
                    ? scrollAndHighlightRow(
                          "summary-balance-sheet",
                          "Total Liabilities",
                      )
                    : handleMetricClick(totalLiabilities, "Total Liabilities"),
        },
        {
            label: createMetricLabel(
                "Accounts Receivable",
                accountsReceivable?.info.derived.localLabel,
            ),
            value: accountsReceivableColorized.value,
            valueClassName: accountsReceivableColorized.className,
            source: accountsReceivableColorized.source,
            sourceClassName: accountsReceivableColorized.sourceClassName,
            delta: accountsReceivable?.delta,
            onClick: () =>
                showM05
                    ? scrollAndHighlightRow(
                          "summary-balance-sheet",
                          "Accounts Receivable",
                      )
                    : handleMetricClick(
                          accountsReceivable,
                          "Accounts Receivable",
                      ),
        },
        {
            label: createMetricLabel(
                "Accounts Payable",
                accountsPayable?.info.derived.localLabel,
            ),
            value: accountsPayableColorized.value,
            valueClassName: accountsPayableColorized.className,
            source: accountsPayableColorized.source,
            sourceClassName: accountsPayableColorized.sourceClassName,
            delta: accountsPayable?.delta,
            onClick: () =>
                showM05
                    ? scrollAndHighlightRow(
                          "summary-balance-sheet",
                          "Accounts Payable",
                      )
                    : handleMetricClick(accountsPayable, "Accounts Payable"),
        },
        {
            label: createMetricLabel(
                "Inventory",
                inventory?.info.derived.localLabel,
            ),
            value: inventoryColorized.value,
            valueClassName: inventoryColorized.className,
            source: inventoryColorized.source,
            sourceClassName: inventoryColorized.sourceClassName,
            delta: inventory?.delta,
            onClick: () =>
                showM05
                    ? scrollAndHighlightRow(
                          "summary-balance-sheet",
                          "Inventory",
                      )
                    : handleMetricClick(inventory, "Inventory"),
        },
        {
            label: createMetricLabel(
                "Current Debt Balance",
                currentDebt?.info.derived.localLabel,
            ),
            value: currentDebtColorized.value,
            valueClassName: currentDebtColorized.className,
            source: currentDebtColorized.source,
            sourceClassName: currentDebtColorized.sourceClassName,
            delta: currentDebt?.delta,
            onClick: () =>
                showM05
                    ? scrollAndHighlightRow(
                          "summary-balance-sheet",
                          "Current Debt Balance",
                      )
                    : handleMetricClick(currentDebt, "Current Debt Balance"),
        },
    ];
}
